/**
 * Branching Rules
 *
 * Centralized business logic that determines navigation overrides
 * after a step submit. These stay in code (not CMS-configurable)
 * because they encode complex business decisions.
 *
 * Evaluated after API contracts fire. Returns a redirect path
 * if the normal nextRoute should be overridden, or null to proceed normally.
 */
import { QuoteTypeOfDwellingEnum } from "@hunkware/shared/types";
import { isTargetDateToday } from "./api-contracts";
import { readStorePath } from "./store-accessors";
import { routePaths } from "../../configs";
import { CategoryIdEnum } from "../../types";
import { appointmentApi } from "../../../entities/appointment";
import { $quoteCalculation } from "../../../entities/quote";
import { wizardApi } from "../../../entities/wizard";
import { $manifestCapabilities } from "./manifest-capabilities";

type BranchingContext = {
  stepId: string;
  categorySlug: string;
};

/**
 * Evaluate branching rules for the current step.
 * Returns a redirect path override, or null to proceed normally.
 */
export const evaluateBranchingRules = (
  context: BranchingContext,
): string | null => {
  const { stepId, categorySlug } = context;

  const categoryId = readStorePath("account", "category.id") as
    | number
    | undefined;

  // Labor always goes to confirmation after contact-information
  if (categoryId === CategoryIdEnum.Labor && stepId === "contact-information") {
    return routePaths.confirmation + "?category=" + categorySlug;
  }

  // Business dwelling → confirmation
  if (
    stepId === "dwelling-information" ||
    stepId === "destination-dwelling-information"
  ) {
    const dwellingPath =
      stepId === "dwelling-information"
        ? "origin.dwellingType.id"
        : "destination.dwellingType.id";
    const dwellingType = readStorePath("quote", dwellingPath) as
      | number
      | undefined;

    if (Number(dwellingType) === QuoteTypeOfDwellingEnum.Business) {
      return routePaths.confirmation + "?category=" + categorySlug;
    }
  }

  // bookingType=call → confirmation
  if (stepId === "provide-more-details") {
    const bookingType = readStorePath("bookingProcess", "bookingType") as
      | string
      | undefined;
    if (bookingType === "call") {
      return routePaths.confirmation + "?category=" + categorySlug;
    }
  }

  // Moving, after Special Items: branch on the quoteCalculate result
  // (fired earlier in this same submit's contract evaluation).
  if (stepId === "additional-factors" && categoryId === CategoryIdEnum.Moving) {
    // The bookingConfirmed gate (hidden field on this step) is one-shot:
    // this submit's contracts have already consumed it above, so disarm it.
    // It's session-persisted, and every other quoteCalculate/quoteCreate
    // prerequisite is long since satisfied by now — without this, going back
    // and resubmitting ANY earlier step (e.g. changing the bedroom count)
    // re-fires calculate + create right there and terminates the flow
    // prematurely, skipping the remaining steps. The hidden field re-arms
    // the gate on the next additional-factors submit.
    appointmentApi.set({ bookingConfirmed: false });

    const calculation = $quoteCalculation.getState();

    // Interstate destination not serviced → same not-serviced screen as
    // postal-not-serviced, same copy. No quote/call request — this blocks
    // the booking.
    if (calculation?.interstateDestinationUnserviced) {
      return routePaths.notServiced + "?category=" + categorySlug;
    }

    // No on-site estimate required → old flow: quoteCreate + callRequest
    // already fired above, so just finish at confirmation.
    if (calculation?.onSiteEstimateRequired === false) {
      return routePaths.confirmation + "?category=" + categorySlug;
    }

    // OSE required, but the active workflow manifest has no availability
    // page to render it on (control bucket of the on-site-ose experiment, or
    // a Sanity outage on a stripped build cache) → old flow: quoteCreate +
    // callRequest already fired above (their conditions check the same
    // predicate), finish at the standard confirmation. Never route to a page
    // whose step-config content cannot resolve — that's an infinite loader.
    if (!$manifestCapabilities.getState().movingHasAvailabilityStep) {
      return routePaths.confirmation + "?category=" + categorySlug;
    }

    // On-site estimate required, but target date is today → same-day:
    // quoteCreate + sendSameDayDispatchMessage already fired above (no slot
    // to pick), same confirmation screen as the call-request path.
    if (calculation?.onSiteEstimateRequired === true && isTargetDateToday()) {
      return routePaths.confirmation + "?category=" + categorySlug;
    }

    // On-site estimate required, not same-day, but checkAvailabilityRange
    // found no slot before the target date → no-availability: quoteCreate +
    // sendNoAvailabilityDispatchMessage already fired above (no slot to
    // pick either), same confirmation screen as same-day.
    if (
      calculation?.onSiteEstimateRequired === true &&
      !isTargetDateToday() &&
      calculation?.hasAvailabilityInRange === false
    ) {
      return routePaths.confirmation + "?category=" + categorySlug;
    }

    // On-site estimate required, not same-day, slots exist — advance to the
    // availability step to let the user pick one.
    //
    // Un-skip it synchronously first: the $wizard skip flag is
    // session-persisted, so if a PREVIOUS calculate resolved to the standard
    // track, availability still holds skip:true right now. Track skips are
    // normally rewritten by use-apply-track-skips.ts, but that's an app-entry
    // effect — it runs AFTER the render this navigation triggers, and
    // generic-step's render-time guard (`isManifestReady && !currentStepId`)
    // would bounce off the still-filtered availability route first.
    wizardApi.setSkip({
      service: categorySlug,
      step: routePaths.availability,
      skip: false,
    });
    return routePaths.availability + "?category=" + categorySlug;
  }

  return null;
};
